import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';

import { AuthRoute } from './auth-route';
import { LandingRoute } from './landing-route';
import { AdminRoute } from './admin-route';
import { RouteErrorBoundary } from './RouteErrorBoundary';

const Layout = lazy(() => import('./layout/Layout').then((m) => ({ default: m.Layout })));
const TransactionList = lazy(() =>
    import('@/features/transactions/list/TransactionList').then((m) => ({ default: m.TransactionList })),
);
const TransactionDetail = lazy(() =>
    import('@/features/transactions/detail/TransactionDetail').then((m) => ({
        default: m.TransactionDetail,
    })),
);
const TriageDashboard = lazy(() =>
    import('@/workflows/triage-dashboard/TriageDashboard').then((m) => ({
        default: m.TriageDashboard,
    })),
);
const DossierList = lazy(() =>
    import('@/features/dossiers/list/DossierList').then((m) => ({ default: m.DossierList })),
);
const DossierDetail = lazy(() =>
    import('@/features/dossiers/detail/DossierDetail').then((m) => ({ default: m.DossierDetail })),
);
const DossierCreate = lazy(() =>
    import('@/features/dossiers/create/DossierCreate').then((m) => ({ default: m.DossierCreate })),
);
const DeviceList = lazy(() =>
    import('@/features/devices/list/DeviceList').then((m) => ({ default: m.DeviceList })),
);
const DeviceDetail = lazy(() =>
    import('@/features/devices/detail/DeviceDetail').then((m) => ({ default: m.DeviceDetail })),
);
const Settings = lazy(() =>
    import('@/features/settings/Settings').then((m) => ({ default: m.Settings })),
);
const AuditLog = lazy(() =>
    import('@/features/audit-log/AuditLog').then((m) => ({ default: m.AuditLog })),
);
const Unauthorized = lazy(() =>
    import('./unauthorized/Unauthorized').then((m) => ({ default: m.Unauthorized })),
);
// Cross-slice linking workflow: pickers mount as modal child routes so the
// flow is deep-linkable and survives refresh.
const DossierPicker = lazy(() =>
    import('@/workflows/dossier-transaction-linking/DossierPicker').then((m) => ({
        default: m.DossierPicker,
    })),
);
const TransactionPicker = lazy(() =>
    import('@/workflows/dossier-transaction-linking/TransactionPicker').then((m) => ({
        default: m.TransactionPicker,
    })),
);
const TransactionPreviewDialog = lazy(() =>
    import('@/features/transactions/preview/TransactionPreviewDialog').then((m) => ({
        default: m.TransactionPreviewDialog,
    })),
);

function Fallback() {
    return null;
}

export const routes = [
    {
        path: '/',
        element: <LandingRoute />,
        ErrorBoundary: RouteErrorBoundary,
    },
    {
        path: '/app',
        element: <AuthRoute />,
        ErrorBoundary: RouteErrorBoundary,
        children: [
            {
                element: (
                    <Suspense fallback={<Fallback />}>
                        <Layout />
                    </Suspense>
                ),
                children: [
                    { index: true, element: <Navigate to="dashboard" replace /> },
                    {
                        path: 'transactions',
                        element: (
                            <Suspense fallback={<Fallback />}>
                                <TransactionList />
                            </Suspense>
                        ),
                    },
                    {
                        path: 'transactions/:id',
                        element: (
                            <Suspense fallback={<Fallback />}>
                                <TransactionDetail />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: 'link',
                                element: (
                                    <Suspense fallback={<Fallback />}>
                                        <DossierPicker />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: 'dashboard',
                        element: (
                            <Suspense fallback={<Fallback />}>
                                <TriageDashboard />
                            </Suspense>
                        ),
                    },
                    {
                        path: 'dossiers',
                        element: (
                            <Suspense fallback={<Fallback />}>
                                <DossierList />
                            </Suspense>
                        ),
                    },
                    {
                        path: 'dossiers/new',
                        element: (
                            <Suspense fallback={<Fallback />}>
                                <DossierCreate />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: 'transactions/:transactionId',
                                element: (
                                    <Suspense fallback={<Fallback />}>
                                        <TransactionPreviewDialog />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: 'dossiers/:id',
                        element: (
                            <Suspense fallback={<Fallback />}>
                                <DossierDetail />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: 'link',
                                element: (
                                    <Suspense fallback={<Fallback />}>
                                        <TransactionPicker />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'transactions/:transactionId',
                                element: (
                                    <Suspense fallback={<Fallback />}>
                                        <TransactionPreviewDialog />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: 'devices',
                        element: (
                            <AdminRoute>
                                <Suspense fallback={<Fallback />}>
                                    <DeviceList />
                                </Suspense>
                            </AdminRoute>
                        ),
                    },
                    {
                        path: 'devices/:deviceUuid',
                        element: (
                            <AdminRoute>
                                <Suspense fallback={<Fallback />}>
                                    <DeviceDetail />
                                </Suspense>
                            </AdminRoute>
                        ),
                        children: [
                            {
                                path: 'transactions/:transactionId',
                                element: (
                                    <Suspense fallback={<Fallback />}>
                                        <TransactionPreviewDialog />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: 'settings',
                        element: (
                            <AdminRoute>
                                <Suspense fallback={<Fallback />}>
                                    <Settings />
                                </Suspense>
                            </AdminRoute>
                        ),
                    },
                    {
                        path: 'audit-log',
                        element: (
                            <AdminRoute>
                                <Suspense fallback={<Fallback />}>
                                    <AuditLog />
                                </Suspense>
                            </AdminRoute>
                        ),
                    },
                ],
            },
        ],
    },
    {
        path: '/unauthorized',
        element: (
            <Suspense fallback={<Fallback />}>
                <Unauthorized />
            </Suspense>
        ),
    },
];

export const router = createBrowserRouter(routes);
